import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'airbnb-js-shims'

import '@mdi/font/css/materialdesignicons.css'

// Vue 3 migration Phase 6: @vue/compat is gone. The user-code `vue` import
// resolves to the real Vue 3 package; bootstrap is `createApp(...).use(...).mount('#app')`.
// All previous Vue.use / Vue.component / Vue.mixin / Vue.config.* calls
// have been moved onto the `app` instance.
import { createApp, h } from 'vue'
import jQuery from 'jquery'
import { maybeReloadForStaleChunk } from '@/utils/staleChunk'
import { reportClientError } from '@/utils/clientTelemetry'

// Webpack's ProvidePlugin used to auto-import jQuery into any module that
// referenced a bare `$` or `jQuery`. Vite has no equivalent, so the 5
// files that still rely on jQuery — Froala's iframe plugin
// (src/libs/froala/plugins/iframe/iframe.js), the Wiris quiz instance
// host (WirisQuestionInstance.vue), the drag-and-drop quiz builder
// (DragAndDropOnImageBuildComponent.vue), and the dashboard course-link
// widget — all resolve `$` via the global scope chain. Set the globals
// before Vue mounts so any lazy-loaded chunk that subsequently imports
// those files finds jQuery already in place.
window.$ = window.jQuery = jQuery

// Webpack 4 also defined Node's `global` for every module; Vite does not.
// @daily-co/daily-js (the consultation and video-chat rooms) writes to a bare
// `global` when its chunk loads, so without this the video room failed with
// "global is not defined" (found 2026-11-03). Lazy chunks load after main.js
// has run, so setting it here is early enough.
if (typeof window.global === 'undefined') window.global = window

import './assets/css/tailwind.css'
import '../static/icons/style.css'
// Phase 6: simplebar (core 5.x) is gone; simplebar-vue@2 owns its own CSS.
import 'simplebar-vue/dist/simplebar.min.css'

// Phase 3b: vue-video-player removed; videojs is integrated directly in
// src/components/media/VideoModel.vue (the only call site). Keeping the
// CSS import here so it lands in the global stylesheet for any video tags
// outside the VideoModel scope.
import 'video.js/dist/video-js.css'

// Phase 2.1: LearnUIcon is the v-icon replacement.
import LearnUIcon from '@/components/learnUCommon/LearnUIcon.vue'

// Phase 2.3: LearnUBtn replaces <v-btn>.
import LearnUBtn from '@/components/learnUCommon/LearnUBtn.vue'

// Phase 2.5: lightweight shims for the residual <v-*> tags
// (text-field, select, tooltip, switch, etc.). Plain HTML + Tailwind.
// Vuetify itself is gone as of Phase 2.6 — these shims are now the
// authoritative implementation for any `<v-*>` markup. Phase 6: rewritten
// to v3-native render functions.
import vuetifyShims from '@/plugins/vuetifyShims'

// Phase 3b: <drag>/<drop> shim — replaces vue-drag-drop with a tiny
// HTML5-DnD wrapper. Same external API; preserves the 3 quiz-question
// model templates without changes.
import dragDropShim from '@/plugins/dragDropShim'

// Phase 3b: vue-snotify replaced with vue-toastification + a thin
// `$snotify` adapter. The 25 `spawnSuccessToast`/`spawnErrorToast` callers
// in the global mixin and the 5 direct `this.$snotify.info(...)` callers
// in dialogue.vue all keep working unchanged via the adapter.
import snotifyAdapter from '@/plugins/snotifyAdapter'

// import edaptioDataCollector from '@/plugins/edaptioDataCollector';

/**
 * Froala editor
 */
//import VueFroala from 'vue-froala-wysiwyg'
//import 'froala-editor/css/froala_editor.pkgd.min.css'

//import '@wiris/mathtype-froala3/wiris.js'

// only track bug on staging or production
const isProduction = (import.meta.env.MODE === 'production')
const isStaging = (import.meta.env.MODE === 'staging')

import App from './App'
import auth from './auth'
import axios from 'axios'
// Phase 3b: vue-authenticate replaced with a hand-rolled popup OAuth helper.
// Same `this.$auth.authenticate(provider)` contract; see plugins/oauthAuthenticate.js.
import oauthAuthenticate from '@/plugins/oauthAuthenticate'
import router from './router/mainRouter'
import store from './store/mainStore'
import i18n from './i18n/i18nMain'
import realtimeBus from '@/plugins/realtimeBus'
import realtime from '@/plugins/realtime'
// Legacy shim — ~12 components still reference $dialogueManager; the shim is a
// no-op so they don't throw. See plugins/edaptioDialogue.js for the migration note.
import edaptioDialogue from '@/plugins/edaptioDialogue'
import { createResponseErrorHandler } from '@/plugins/apiResponseInterceptor'
import { installStaleSessionReload } from '@/utils/staleSessionReload'

// Response interceptor — hard logout on 401, and on an authorization 403
// re-read the user and replay once. See plugins/apiResponseInterceptor.js.
axios.interceptors.response.use(
    response => response,
    createResponseErrorHandler({ axios, auth, router, store })
)
// set base API URL
axios.defaults.baseURL = import.meta.env.VITE_BASE_API_URL

// set auth header on start up if token is present
axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('id_token')

// A page restored by Back after "log in as" or logout must not keep acting
// with the other user's token. See utils/staleSessionReload.js.
installStaleSessionReload()

// global navigation guard for authentication
router.beforeEach(async (to, from, next) => {
    // clear all tooltips
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // this route requires auth, check if logged in
        // if not, redirect to login page.
        if (!auth.checkAuth()) {
            next({
                path: '/login',
                query: {redirect: to.fullPath}
            })
        } else {
            next()
        }
    }
    if (to.matched.some(record => record.meta.requiresInstitutionSetting)) {
        let settingName = to.meta.requiresInstitutionSetting.name
        let settingValue = to.meta.requiresInstitutionSetting.value
        let pathIfFail = to.meta.requiresInstitutionSetting.pathIfFail

        if (settingName && settingValue) {
            await store.dispatch(
                'administrationStore/getInstitutionSettings',
                {
                    forceRefresh: true
                })

            let found = false
            for (let setting of store.state.administrationStore.institutionSettings.settings) {
                if (setting.name === settingName && setting.value === settingValue) {
                    found = true
                    break
                }
            }

            found ? next() : next({path: pathIfFail ? pathIfFail : '/403'})
        }
    }
    next() // make sure to always call next()!
})

// Date/time filters were extracted to `src/utils/format.js` as plain
// functions for Vue 3 compatibility. Per-template imports replaced the
// global Vue.filter registrations. See thoughts/shared/plans/2026-04-25-vue3-vite-migration.md
// → Phase 0 #5.

// Auto-recover from stale-chunk errors raised outside Vue Router's
// navigation context. The router has its own onError handler for
// navigation-time failures; these two catch bare dynamic imports. Matching
// and the 30s reload guard live in utils/staleChunk.js, shared with the
// router so a genuinely-broken bundle can't loop.
// A stale chunk is self-healing, so it reloads and is NOT worth an event.
// Anything else that reaches these handlers is a real client-side error that
// until now existed only in a console nobody opens.
window.addEventListener('unhandledrejection', (ev) => {
    if (maybeReloadForStaleChunk(ev.reason)) return
    reportClientError('unhandledrejection', ev.reason)
})
window.addEventListener('error', (ev) => {
    if (maybeReloadForStaleChunk(ev.message || (ev.error && ev.error.message))) return
    reportClientError('window', ev.error || ev.message)
})

// Vue 3 migration Phase 6: bootstrap via createApp + app.use(...) for
// router/store/i18n + every plugin. All previous `Vue.use(...)` /
// `Vue.component(...)` / `Vue.mixin(...)` registrations now run on the
// app instance directly — no compat shim, no Vue constructor.
const app = createApp({
    render() { return h(App) },
})

// The THIRD place a stale chunk surfaces, and the one that was missing until
// 2026-08-14 (FRB VUC: "når jeg skal besvare opgaver på tavlen, kommer pop-up
// vinduet ikke op" — for one student, on one browser, with no error).
//
// A failed `defineAsyncComponent` never reaches the window listeners above.
// Vue's loader catches its own rejection (`.catch(err => { onError(err);
// error.value = err })`) and routes it through handleError(…,
// ASYNC_COMPONENT_LOADER), which lands here. With no handler installed it
// fell through to a bare console warning: the component rendered nothing, the
// tab never reloaded, and the UI just looked broken. Every lazily-loaded
// component in the app was exposed — most of the modals among them.
//
// NOTE: installing a handler SUPPRESSES Vue's own logError (handleError
// returns early once errorHandler exists), so this must log everything it
// doesn't act on, or all component errors go silent.
app.config.errorHandler = (err, instance, info) => {
    if (maybeReloadForStaleChunk(err)) return
    console.error(`[vue] error during ${info}:`, err)
    reportClientError('vue', err, { info: String(info || '').slice(0, 120) })
}

if (!isProduction) {
    app.config.performance = true
}

app.component('LearnUIcon', LearnUIcon)
app.component('LearnUBtn', LearnUBtn)

app.use(router)
app.use(store)
app.use(i18n)
app.use(vuetifyShims)
app.use(dragDropShim)
app.use(snotifyAdapter)
app.use(realtimeBus)
app.use(realtime)
app.use(edaptioDialogue)
app.use(oauthAuthenticate)

// Phase 6.9: the global mixin's 27 methods + 12 computeds were extracted
// into pure utility functions in `src/utils/globals.js` + `src/utils/format.js`.
// The mixin still installs (so existing Options API call sites keep
// accessing helpers via `this.X`), but each method/computed now delegates
// to the pure function. Behavior is preserved 1:1.
//
// New `<script setup>` components should import `useGlobals()` from
// `src/composables/useGlobals.js` instead of relying on the mixin.
//
// The mixin object itself lives in `src/globalMixin.js` so unit tests can
// install the real one rather than re-implementing the handful of helpers
// each component happens to call.
import globalMixin from '@/globalMixin'

app.mixin(globalMixin)

app.mount('#app')
